import { createSlice } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';
import { CONNECTIVITY_DASHBOARD_API_PATHS, DEFAULT_ROLES } from '../../utils/user-management';

const page_name = 'profile';

export interface passwordStates {
  isUserDataLoading: boolean;
  isUserDataReqSuccess: boolean;
  isUserDataReqFailed: boolean;
  userDetails: any;
  userRole: any;
  userAccess: any;
  isUserPasswordLoading: boolean;
  userPassword: Array<any>;
  userPasswordError: boolean;
  isUpdateUserPasswordLoading: boolean;
  isUpdateUserPasswordSuccess: boolean;
  isUpdateUserPasswordError: boolean;
  isInvalidPassword: boolean;
  profileMessage: any;
}

const initialState: passwordStates = {
  isUserDataLoading: false,
  isUserDataReqSuccess: false,
  isUserDataReqFailed: false,
  userDetails: {},
  userRole: {},
  userAccess: {},
  isUserPasswordLoading: false,
  userPassword: [],
  userPasswordError: false,
  isUpdateUserPasswordLoading: false,
  isUpdateUserPasswordSuccess: false,
  isUpdateUserPasswordError: false,
  isInvalidPassword: false,
  profileMessage: '',
};

const UserPasswordReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getUserProfileDataRequest: (state, { payload }: { payload: string }) => {
      state.isUserDataLoading = true;
      state.isUserDataReqSuccess = false;
      state.isUserDataReqFailed = false;
    },
    getUserProfileDataResponse: (state, action: any) => {
      state.isUserDataLoading = false;
      state.isUserDataReqSuccess = true;
      state.userDetails = get(action, 'payload.res', {});

      const roleData = get(action, 'payload.res.roles', []).filter(
        (role: any) => !DEFAULT_ROLES.includes(role.name)
      )[0];
      const { attributes, ...remainingData } = roleData;

      state.userRole = remainingData;

      const connectivityDashboardAccess: any = {};

      const accesses = Object.entries(attributes).reduce((acc: any, [name, value]: any) => {
        const decodedValue = (dec: number) => (parseInt(value, 10) & dec) === dec;
        if (CONNECTIVITY_DASHBOARD_API_PATHS.includes(name)) {
          connectivityDashboardAccess[name] = decodedValue(1);
          return {
            ...acc,
            'things/connectivity_dashboard': {
              all: false,
              read: false,
              create: false,
              update: false,
              delete: false,
              approver: false,
            },
          };
        } else {
          if (process.env.REACT_APP_API_THINGS_GATEWAYS === name) {
            connectivityDashboardAccess[name] = decodedValue(1);
          }
          return {
            ...acc,
            [name]: {
              all: parseInt(value, 10) === 31,
              read: decodedValue(1),
              create: decodedValue(4),
              update: decodedValue(2),
              delete: decodedValue(8),
              approver: decodedValue(16),
            },
          };
        }
      }, {});
      state.userAccess = Object.keys(accesses).reduce((acc: any, item: any) => {
        if (item === 'things/connectivity_dashboard') {
          return {
            ...acc,
            [item]: {
              ...accesses[item],
              read: [...CONNECTIVITY_DASHBOARD_API_PATHS, process.env.REACT_APP_API_THINGS_GATEWAYS]
                .map((acc: any) => connectivityDashboardAccess[acc] ?? false)
                .every((acc) => acc),
            },
          };
        }
        return {
          ...acc,
          [item]: accesses[item],
        };
      }, {});
    },
    getUserProfileDataFailed: (state, action: any) => {
      state.isUserDataLoading = false;
      state.isUserDataReqFailed = true;
    },
    getUserPassword: (state, action: { payload: string }) => {
      state.isUpdateUserPasswordSuccess = false;
      state.isUserPasswordLoading = true;
      state.isInvalidPassword = false;
    },
    getUserPasswordResponse: (state, action) => {
      state.isUserPasswordLoading = false;
      state.userPassword = action.payload.res;
    },
    getUserPasswordFailed: (state, action: any) => {
      state.isUserPasswordLoading = false;
      state.userPasswordError = true;
      state.userPassword = [];

      if (action.payload === 422) {
        state.isInvalidPassword = true;
      }
    },
    updateUserPasswordRequest: (state) => {
      // state.isUserPasswordLoading = true;
      state.isUpdateUserPasswordLoading = true;
      state.isUpdateUserPasswordSuccess = false;
      state.isUpdateUserPasswordError = false;
    },
    updateUserPasswordResponse: (state, action: any) => {
      state.isUpdateUserPasswordLoading = false;
      state.isUpdateUserPasswordSuccess = true;
      state.isUserPasswordLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.profileMessage = message;
      Snackbar.success(message);
    },
    updateUserPasswordFailed: (state, action: any) => {
      state.isUpdateUserPasswordLoading = false;
      state.isUpdateUserPasswordError = true;
      state.isUserPasswordLoading = false;

      const message = getStatusMessage(page_name, action.payload);
      state.profileMessage = message;
      Snackbar.error(message);
    },
    resetLoadingState: (state) => {
      state.isUpdateUserPasswordSuccess = false;
      state.isUpdateUserPasswordError = false;
      state.isUserPasswordLoading = false;
      state.profileMessage = '';
      state.userPasswordError = false;
    },
    resetInvalidPassword: (state) => {
      state.isUpdateUserPasswordError = false;
    },
  },
});

export const userPasswordActions = UserPasswordReducer.actions;
export default UserPasswordReducer;
